/**
 * 失败重试(整体重试)提交前要取的**正式报价单**(修复波 0926 第三批 · 代理 H)。
 *
 * ★为什么要有它:后端 `POST /poster/tasks/{id}/retry` 早就收 `{quoteId}`(0922 丢单点 b),
 *   但详情页「整体重试」和「我的作品」列表的重试两个入口都**只发空 body** —— 整条落在
 *   `poster_task_without_quote` 软着陆 WARN。
 *
 * ★张数 = 任务落库的 `requestedCount`:后端 retry 的余额闸就是拿它算的
 *   (`PosterService.retry` → `precheckCreditsFromPayload(…, t.getRequestedCount())`),
 *   重试是「重发整包」,张数与首发同一个数(单张/多张 = 张数;多比例 = 去重后的比例数,
 *   create 时已按 computeRequestedCount 落库)。
 *
 * ★三类**刻意不取单**(返回 null → 照旧软着陆,不阻断重试):
 *   · LONG_IMAGE:按屏计费,屏数前端算不准;后端自己也只在屏数精确时开合同
 *     (`quoteForServerBuiltCreate`「下界不开合同」),前端更不能拿估的数开。
 *   · GROUP:续出会改写落库 payload(groupStage→all / requestedCount→剩余张数),
 *     任务行上的 requestedCount 与这次重发真出几张不保证同一个数,单开错了就是按错的数兑现。
 *   · TRYON:换衣的结算动作 id 是 `tryon(taskId, regenId)` 形态,不是这里绑的
 *     `poster(taskId)`;绑上去结算也找不到,还白占一张单。
 *   · 另:缺 provider / 缺档位 / 张数 ≤ 0 —— 不猜档位(猜错=按错档兑现)。
 */
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import type { QuoteParams } from "@/entities/pricing/api/pricingService";
import { acquireQuoteId } from "@/entities/pricing/model/useQuote";

import { posterService, type PosterTaskDto } from "../api/posterService";

export type RetryQuoteTask = Pick<
  PosterTaskDto,
  "provider" | "resolutionTier" | "requestedCount" | "posterKind"
>;

const NO_QUOTE_KINDS = new Set(["LONG_IMAGE", "GROUP", "TRYON"]);

export function retryQuoteParams(task: RetryQuoteTask | null | undefined): QuoteParams | null {
  if (!task) return null;
  if (task.posterKind && NO_QUOTE_KINDS.has(task.posterKind)) return null;
  const qty = task.requestedCount;
  if (!task.provider || !task.resolutionTier || !qty || qty <= 0) return null;
  return {
    skuCode: SKU.posterImage,
    variantKey: imageVariant(task.provider, task.resolutionTier),
    qty,
  };
}

/**
 * 整体重试:先取单再重试。
 *
 * @param task 手上已有的任务详情(详情页);不给就现拉一次 `getTask`(列表页的卡上没有
 *   provider/档位/形制)。拉不到 / 取单失败 → 不带单照常重试(后端软着陆 WARN),
 *   不把「报价口抖了一下」变成「重试点不动」。
 */
export async function retryPosterTask(
  taskId: number,
  task?: RetryQuoteTask | null
): Promise<{ taskId: number }> {
  let source: RetryQuoteTask | null | undefined = task;
  if (source === undefined) {
    try {
      source = await posterService.getTask(taskId);
    } catch {
      source = null;
    }
  }
  const quoteId = await acquireQuoteId(retryQuoteParams(source));
  return posterService.retryTask(taskId, quoteId);
}
